import { FunderConfig, WidgetStyles } from './customization';

/**
 * Convierte el segmento de ruta del widget (p. ej. `sms`, `clavepin`) al nombre
 * de flujo que usa el API en `/users/{id}/customization/flow/{Flow}`.
 * Si no se reconoce, devuelve `SMS` como pidió el producto.
 */
export function widgetRouteSegmentToApiFlowName(segment: string | undefined): string {
  if (!segment) {
    return 'SMS';
  }
  const key = segment.trim().toLowerCase().replace(/-/g, '');
  const map: Record<string, string> = {
    sms: 'SMS',
    clavepin: 'ClavePin',
    clavepinqr: 'ClavePinQr',
    clavelpin: 'ClavePin',
    smspin: 'SmsPin',
    videoid: 'VideoID',
    chavemovel: 'ChaveMovel',
    presencial: 'KnownClient',
    knownclient: 'KnownClient',
    clientcert: 'ClientCertificate',
    clientcertificate: 'ClientCertificate',
    clientcertmock: 'ClientCertificate',
    // El flow de customización en backend sigue llamándose "alemany"
    // (GET /users/{id}/customization/flow/alemany); el resto del widget
    // usa "local-certificate".
    localcertificate: 'alemany',
    digitalpower: 'DigitalPower',
    poderdigital: 'PowerActivate',
    manual: 'Manual',
    manualstep: 'Manual',
  };
  return map[key] ?? 'SMS';
}

/**
 * Intenta obtener `config` y `styles` del cuerpo de
 * `GET .../customization/flow/{Flow}` (misma forma que expone el backend agregado).
 */
export function extractFunderCustomizationFromFlowResponse(resp: unknown): {
  config: Partial<FunderConfig> | null;
  styles: Partial<WidgetStyles> | null;
} {
  if (!resp || typeof resp !== 'object') {
    return { config: null, styles: null };
  }
  const r = resp as Record<string, unknown>;
  const data = r['data'];
  let dataObj: Record<string, unknown> | null = null;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    dataObj = data as Record<string, unknown>;
  }

  const topConfig = r['config'];
  const topStyles = r['styles'];
  const nestedConfig = dataObj?.['config'];
  const nestedStyles = dataObj?.['styles'];

  const config =
    (topConfig && typeof topConfig === 'object'
      ? (topConfig as Partial<FunderConfig>)
      : null) ??
    (nestedConfig && typeof nestedConfig === 'object'
      ? (nestedConfig as Partial<FunderConfig>)
      : null);

  const styles =
    (topStyles && typeof topStyles === 'object'
      ? (topStyles as Partial<WidgetStyles>)
      : null) ??
    (nestedStyles && typeof nestedStyles === 'object'
      ? (nestedStyles as Partial<WidgetStyles>)
      : null);

  return { config, styles };
}

/**
 * Configuración mínima del financiador cuando no existe respuesta del API
 * o falta `funder_id`, para que el widget siga cargando el flujo.
 */
export function createDefaultFunderConfig(userId: string): FunderConfig {
  const id = userId || '';
  return {
    mode: 'SMS',
    attributes: {
      withDni: true,
      withBirthday: true,
      withExpiration: true,
      withPhone: true,
    },
    followMail: false,
    dataAssignmentCheck: false,
    dataAssignmentText: '',
    forceChecks: false,
    infiniteLoopInCompleted: false,
    dontShowDataInTerms: false,
    noRetryInBadPhone: false,
    hideCheckLegal: false,
    introEnabled: false,
    tryPinWithoutSMS: false,
    typography: '',
    activatePower: false,
    disableUrlWidget: false,
    funder_id: id,
    policy: '',
  };
}

/**
 * Estilos por defecto (tema neutro) cuando no existe customización de estilos.
 * Los iconos usan los literales que espera SweetAlert / NotificationsService.
 */
export function createDefaultWidgetStyles(): WidgetStyles {
  return {
    colors: {
      title: '#1a1a1a',
      background: '#ffffff',
      text: '#333333',
      btnBg: '#3f51b5',
      btnText: '#ffffff',
      dangerBgColor: '#f44336',
      dangerColor: '#ffffff',
      dangerTextButtonColor: '#ffffff',
      accentBgColor: '#e3f2fd',
      accentColor: '#1976d2',
    },
    btnRound: 8,
    logoUrl: '',
    withLogo: false,
    successIcon: 'success',
    warningIcon: 'warning',
    errorIcon: 'error',
    infoIcon: 'info',
  };
}

export function mergeFunderConfig(
  api: Partial<FunderConfig> | null | undefined,
  userId: string
): FunderConfig {
  const defaults = createDefaultFunderConfig(userId);
  if (!api || typeof api !== 'object') {
    return defaults;
  }
  return {
    ...defaults,
    ...api,
    funder_id: api.funder_id || userId || defaults.funder_id,
    attributes: {
      ...defaults.attributes,
      ...(api.attributes || {}),
    },
  };
}

export function mergeWidgetStyles(
  api: Partial<WidgetStyles> | null | undefined
): WidgetStyles {
  const defaults = createDefaultWidgetStyles();
  if (!api || typeof api !== 'object') {
    return defaults;
  }
  return {
    ...defaults,
    ...api,
    colors: {
      ...defaults.colors,
      ...(api.colors || {}),
    },
  };
}

/** Bloque title/description + campos opcionales usados en plantillas del widget */
function textBlock(
  title: string,
  description: string,
  extra: Record<string, unknown> = {}
): Record<string, unknown> {
  return { title, description, customImage: '', ...extra };
}

/**
 * Textos mínimos del flujo (SMS y resto) para evitar `null.texts` en plantillas.
 */
export function createDefaultFlowTexts(): Record<string, any> {
  const formBase = {
    ...textBlock('Datos de la solicitud', 'Introduzca la información solicitada.'),
    firstSentence: '',
    secondSentence: '',
    thirdSentence: '',
    policyActionText: 'Política de privacidad',
    termsActionText: 'Términos y condiciones',
    titleSize: 22,
    aditionalDescription: '',
  };
  return {
    form: formBase,
    requisites: textBlock(
      'Requisitos para continuar',
      '<p class="m-0">Para continuar con el proceso, necesitamos ejecutar el programa FDE que debe tener instalado. Con este programa vamos a utilizar el certificado digital para obtener la información requerida.</p>'
    ),
    fdeValidation: textBlock(
      'No detectamos que FDE se haya abierto',
      '<p class="m-0">Asegúrate de tener <strong>FDE</strong> instalado y en ejecución. Descárgalo si hace falta y vuelve a intentarlo.</p>'
    ),
    success: textBlock('Completado', 'El proceso se ha completado correctamente.'),
    fail: textBlock('Error', 'No se ha podido completar la operación. Inténtelo de nuevo.'),
    tyc: textBlock('Términos y condiciones', ''),
    legalCheck: textBlock('Revisión', ''),
    otpSS: textBlock('Código de verificación', 'Introduzca el código recibido por SMS.'),
    otpAEAT: textBlock('Código de verificación', 'Introduzca el código recibido.'),
    otpIRS: textBlock('Código de verificación', ''),
    otpSSD: textBlock('Código de verificación', ''),
    otpBDE: textBlock('Código de verificación', ''),
    otpCC: textBlock('Código de verificación', ''),
    // Keep QR defaults empty so existing localized labels remain the fallback when a
    // funder's older configuration does not contain either QR screen.
    qr: textBlock('', ''),
    qrSeguridadSocial: textBlock('', ''),
    errorPhone: textBlock('Teléfono incorrecto', 'Revise el número de teléfono e inténtelo de nuevo.'),
    noService: textBlock('Servicio no disponible', 'En este momento no podemos continuar con la solicitud.'),
    abandoned: textBlock('Solicitud abandonada', ''),
    standby: {
      ...textBlock('Solicitud en espera', 'Su solicitud está en espera.'),
      titleSize: 20,
    },
    noPhone: textBlock('Sin teléfono registrado', 'No encontramos un teléfono asociado.'),
    autofirma: textBlock('', ''),
    vintegrisLegal: textBlock('', ''),
    password: textBlock('', ''),
    dataAssignmentText: '',
  };
}

const defaultFlowRootFields: Record<string, unknown> = {
  terms_and_conditions: '<p>Debe aceptar los términos para continuar.</p>',
  widgetUrl: '',
  iframeCode: '',
  packID: '',
  enableUrlCallBack: false,
  automaticRedirectCallBack: false,
  callBackUrl: '',
  btnTextCallBack: '',
  showAditionalDescription: false,
};

export function deepMergeRecords(
  base: Record<string, any>,
  patch: Record<string, any> | null | undefined
): Record<string, any> {
  if (!patch || typeof patch !== 'object') {
    return { ...base };
  }
  const out: Record<string, any> = { ...base };
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    const bv = out[key];
    if (pv === undefined) {
      continue;
    }
    if (pv === null) {
      continue;
    }
    if (
      typeof pv === 'object' &&
      !Array.isArray(pv) &&
      pv !== null &&
      typeof bv === 'object' &&
      bv !== null &&
      !Array.isArray(bv)
    ) {
      out[key] = deepMergeRecords(bv, pv);
    } else {
      out[key] = pv;
    }
  }
  return out;
}

/**
 * Garantiza estructura de `data` del endpoint `.../customization/flow/{Flow}`
 * con `texts` y subbloques siempre definidos.
 */
export function mergeFlowCustomizationWithDefaults(
  apiData: Record<string, any> | null | undefined
): Record<string, any> {
  const textsDefaults = createDefaultFlowTexts();
  const base: Record<string, any> = {
    ...defaultFlowRootFields,
    texts: textsDefaults,
  };
  if (!apiData || typeof apiData !== 'object') {
    return base;
  }
  const mergedTexts = deepMergeRecords(
    textsDefaults,
    apiData.texts && typeof apiData.texts === 'object' ? apiData.texts : {}
  );
  const cfgTranslate =
    apiData.config_translate && typeof apiData.config_translate === 'object'
      ? apiData.config_translate
      : null;
  const daText = mergedTexts.dataAssignmentText;
  if (
    cfgTranslate?.dataAssignmentText &&
    (daText == null || daText === '')
  ) {
    mergedTexts.dataAssignmentText = cfgTranslate.dataAssignmentText;
  }
  return deepMergeRecords(base, { ...apiData, texts: mergedTexts });
}

/** Texto del check de cesión de datos: prioriza traducción del flujo sobre config global. */
export function resolveDataAssignmentText(
  widgetData: Record<string, any> | null | undefined,
  flowKey: string
): string {
  if (!widgetData || !flowKey) {
    return '';
  }
  const flow = widgetData[flowKey];
  const texts = flow?.texts;
  return (
    texts?.dataAssignmentText ||
    flow?.config_translate?.dataAssignmentText ||
    widgetData.config?.dataAssignmentText ||
    ''
  );
}

/**
 * Rellena en `widgetData` las propiedades planas que consumen pasos genéricos
 * (success, fail, tyc, form, otp…) a partir de `widgetData[flowKey].texts`.
 */
export function applyDerivedWidgetStepTexts(
  widgetData: Record<string, any>,
  flowKey: string
): void {
  if (!widgetData || !flowKey) {
    return;
  }
  const colors = widgetData.styles?.colors;
  if (colors) {
    if (widgetData.colors_title == null || widgetData.colors_title === '') {
      widgetData.colors_title = colors.title;
    }
    if (widgetData.colors_text == null || widgetData.colors_text === '') {
      widgetData.colors_text = colors.text;
    }
    if (widgetData.colors_btnText == null || widgetData.colors_btnText === '') {
      widgetData.colors_btnText = colors.btnText;
    }
    if (widgetData.colors_btnBg == null || widgetData.colors_btnBg === '') {
      widgetData.colors_btnBg = colors.btnBg;
    }
  }
  if (
    widgetData.styles &&
    (widgetData.colors_btnRound == null || widgetData.colors_btnRound === '')
  ) {
    widgetData.colors_btnRound = widgetData.styles.btnRound;
  }

  const t = widgetData[flowKey]?.texts;
  if (!t) {
    return;
  }
  const setIf = (field: string, v: unknown) => {
    if (widgetData[field] == null || widgetData[field] === '') {
      widgetData[field] = v ?? '';
    }
  };
  setIf('texts_form_title', t.form?.title ?? 'Datos');
  setIf('texts_form_description', t.form?.description ?? '');
  setIf('texts_success_title', t.success?.title ?? 'Completado');
  setIf('texts_success_description', t.success?.description ?? '');
  setIf('texts_fail_title', t.fail?.title ?? 'Error');
  setIf('texts_fail_description', t.fail?.description ?? '');
  setIf('texts_tyc_title', t.tyc?.title ?? 'Términos y condiciones');
  setIf('texts_tyc_description', t.tyc?.description ?? '');
  setIf(
    'texts_otp_title',
    t.otpSS?.title ?? t.otpAEAT?.title ?? 'Código de verificación'
  );
  setIf(
    'texts_otp_description',
    t.otpSS?.description ?? t.otpAEAT?.description ?? ''
  );
  setIf('texts_errorPhone_title', t.errorPhone?.title ?? '');
  setIf('texts_errorPhone_description', t.errorPhone?.description ?? '');
  setIf('texts_legalCheck_title', t.legalCheck?.title ?? '');
  setIf('texts_legalCheck_description', t.legalCheck?.description ?? '');

  const assignmentText = resolveDataAssignmentText(widgetData, flowKey);
  if (assignmentText && widgetData.config) {
    widgetData.config = { ...widgetData.config, dataAssignmentText: assignmentText };
  }
}
